// @ts-nocheck
import moment from 'moment';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { BoxV2 } from 'sonata-react-components';
import { getCountryName } from 'sonata-static-storage/lib/helpers/countries.helpers';
import { normalize, normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { formatAdsFormat, getUniquesAdsFormat } from '@/helpers/reporting.helpers';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { ActionButton } from '../components/ActionButton';
import { Detail } from '../components/Detail';
import { Logo } from '../components/Logo';
import { BoxStyled } from '../header.styles';
import './styles.scss';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';

const getId = (value: string) => `reporting.header.single.${value}`;

type Props = {
  currencyInfo: BaseCurrencyInterface;
  data: Record<string, any>;
};

function SingleCampaign({ currencyInfo, data }: Props) {
  const { formatMessage } = useIntl();

  const userRoles = useSelector(getUserRoles);
  const user = useSelector(getCurrentUser);

  const [{ status }, setState] = useState({ status: 'not-expanded' });
  const {
    ads = [],
    beginDate,
    bidModels,
    budget,
    countries,
    endDate,
    mediaAdvertiser,
    name,
    overallGoal = {
      target: '',
      type: ''
    }
  } = data;
  const {
    bid: { price, pricingModel }
  } = bidModels[0];
  const { name: advertiser } = mediaAdvertiser || {
    name: formatMessage({
      defaultMessage: 'No Advertiser',
      id: getId('details.noAdvertiser')
    })
  };
  const { target: goalTarget, type: goalType } = overallGoal;

  const typeOfAdsFormat = getUniquesAdsFormat(ads);

  const formatDate = (date: string) => moment(date).utc().format('DD-MM-YYYY');
  const formatPricingModel = (model: string) => model.replace(/^(f-|d-)/, '').toUpperCase();
  const formatCpmPrice = (value: number, type: string) => (type.toLowerCase().includes('cpm') ? value * 1000 : value);

  const formattedBeginDate = formatDate(beginDate);
  const formattedEndDate = formatDate(endDate);
  const formattedGoal = normalize(goalTarget, 0, currencyInfo.countries![0]);
  const formattedGoalType = goalType
    ? formatMessage({
        defaultMessage: goalType,
        id: `reporting.header.goals.${goalType.toLowerCase()}`
      })
    : undefined;
  const formattedPricingModel = formatPricingModel(pricingModel);
  const formattedPricingValue = formatCpmPrice(price, formattedPricingModel);
  const formattedPricing = normalizeWithUnit(formattedPricingValue, currencyInfo, 2);
  const formattedBudget = normalizeWithUnit(budget, currencyInfo, 0);
  const formattedAdsFormat = formatAdsFormat(typeOfAdsFormat);
  const formattedCountries = countries.map(getCountryName).join(', ');

  const updateStatus = () =>
    setState({
      status: status === 'not-expanded' ? 'expanded' : 'not-expanded'
    });

  const goal = goalType ? `${formattedGoal} - ${formattedGoalType}` : formatMessage({ id: getId('details.noGoal') });

  const logoPermissions = checkUserResourcePermissions('reportingLogo', user, userRoles);
  const budgetPermissions = checkUserResourcePermissions('reportingBudget', user, userRoles);
  const pricingPermissions = checkUserResourcePermissions('reportingPricing', user, userRoles);

  return (
    <section className="singleHeader">
      {status === 'expanded' ? (
        <>
          {logoPermissions && <Logo data={[data]} />}
          <div className="singleHeader__description">
            <div className="singleHeader__descriptionInfo">
              <span className="singleHeader__descriptionInfoTitle">{name}</span>
              <span className="singleHeader__descriptionInfoItem">
                <Detail
                  label={formatMessage({
                    defaultMessage: 'Campaign date: ',
                    id: getId('details.date')
                  })}
                  value={`${formattedBeginDate} - ${formattedEndDate}`}
                />
              </span>
              <span className="singleHeader__descriptionInfoItem">
                <Detail
                  label={formatMessage({
                    defaultMessage: 'Country: ',
                    id: getId('details.country')
                  })}
                  value={formattedCountries}
                />
              </span>
            </div>
            <div className="singleHeader__descriptionDetails">
              <BoxStyled borderColor="boxBorderColor01" header="single" id="multiHeader-box" marginRight="16">
                <BoxV2.Body className="header__descriptionBoxDetailsBody" padding="8px">
                  <Detail
                    icon="far fa-tags"
                    label={formatMessage({
                      defaultMessage: 'Advertiser',
                      id: getId('details.advertiser')
                    })}
                    showTitle
                    value={advertiser}
                  />
                </BoxV2.Body>
              </BoxStyled>
              <BoxStyled borderColor="boxBorderColor01" header="single" id="multiHeader-box" marginRight="16">
                <BoxV2.Body className="header__descriptionBoxDetailsBody" padding="8px">
                  <Detail
                    icon="far fa-box"
                    label={formatMessage({
                      defaultMessage: 'Ad type',
                      id: getId('details.adType')
                    })}
                    value={formattedAdsFormat.join(', ')}
                  />
                </BoxV2.Body>
              </BoxStyled>
              <BoxStyled borderColor="boxBorderColor01" header="single" id="multiHeader-box" marginRight="16">
                <BoxV2.Body className="header__descriptionBoxDetailsBody" padding="8px">
                  <Detail
                    icon="far fa-flag"
                    label={formatMessage({
                      defaultMessage: 'Goal',
                      id: getId('details.goal')
                    })}
                    value={goal}
                  />
                </BoxV2.Body>
              </BoxStyled>
              {pricingPermissions && (
                <BoxStyled borderColor="boxBorderColor01" header="single" id="multiHeader-box" marginRight="16">
                  <BoxV2.Body className="header__descriptionBoxDetailsBody" padding="8px">
                    <Detail
                      icon="far fa-coin"
                      label={formatMessage({ id: getId('details.pricing') })}
                      value={`${formattedPricing} - ${formattedPricingModel}`}
                    />
                  </BoxV2.Body>
                </BoxStyled>
              )}
              {budgetPermissions && (
                <BoxStyled borderColor="boxBorderColor01" header="single" id="multiHeader-box" marginRight="16">
                  <BoxV2.Body className="header__descriptionBoxDetailsBody" padding="8px">
                    <Detail
                      icon="far fa-wallet"
                      label={formatMessage({ id: getId('details.budget') })}
                      value={formattedBudget}
                    />
                  </BoxV2.Body>
                </BoxStyled>
              )}
              <ActionButton icon="far fa-minus" onClick={updateStatus} />
            </div>
          </div>
        </>
      ) : (
        <div className="singleHeaderCompact">
          <span className="singleHeaderCompact__descriptionInfoTitle">{name}</span>
          {budgetPermissions && (
            <BoxStyled borderColor="boxBorderColor01" header="single" id="multiHeader-box">
              <BoxV2.Body className="header__descriptionBoxDetailsBody" padding="8px">
                <Detail
                  icon="far fa-wallet"
                  label={formatMessage({ id: getId('details.budget') })}
                  value={formattedBudget}
                />
              </BoxV2.Body>
            </BoxStyled>
          )}
          <ActionButton icon="far fa-plus" onClick={updateStatus} />
        </div>
      )}
    </section>
  );
}

export default SingleCampaign;
